import axios, { type AxiosError } from 'axios';
import { authService } from '@/auth/authService';
import { spaConfig } from '@/config/spaConfig';
import { trackException } from '@/telemetry/appInsights';

/**
 * HTTP client for the Clients API (shared with the mobile app and other services:
 * endpoints and payloads must stay exactly as they are).
 *
 * The bearer token is attached only here, never to third-party calls (Nexi, CDNs, ...).
 * Anonymous pages (QR landing, Vendon result, registration) simply get no token.
 */
export const clientsApi = axios.create();

clientsApi.interceptors.request.use(async (config) => {
  config.baseURL = spaConfig().clientsApiUrl;
  const token = await authService.acquireToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

clientsApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    trackException(error, { url: error.config?.url, status: error.response?.status });
    // Always reject: callers decide how to present the failure (no silent `undefined`).
    return Promise.reject(error);
  },
);

/** Problem-details message from the API, if any (ASP.NET returns `Detail`/`detail`). */
export function apiErrorDetail(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const data = error.response?.data as Record<string, unknown> | undefined;
  const detail = data?.Detail ?? data?.detail ?? data?.error;
  return typeof detail === 'string' && detail.length > 0 ? detail : undefined;
}
